import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { getStatusBadgeClass } from "../config/uiHelpers";

const EmailMarketing = ({ user, logout }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    subject: "",
    html_content: "",
    recipients: ""
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/email-campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error("Failed to fetch email campaigns");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const recipients = newCampaign.recipients.split(',').map(email => email.trim()).filter(email => email);
      await axios.post(`${API}/email-campaigns`, {
        subject: newCampaign.subject,
        html_content: newCampaign.html_content,
        recipients,
        created_by: user.user_id
      });
      toast.success("Email campaign created!");
      setShowCreate(false);
      setNewCampaign({ subject: "", html_content: "", recipients: "" });
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to create email campaign");
    }
  };

  const sendCampaign = async (campaignId) => {
    try {
      const response = await axios.post(`${API}/email-campaigns/${campaignId}/send`);
      toast.success(response.data.message);
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to send campaign");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                Email Marketing
              </h1>
              <p className="text-base leading-relaxed text-slate-700">
                Create and send email campaigns
              </p>
            </div>
            <button
              data-testid="create-email-campaign-btn"
              onClick={() => setShowCreate(!showCreate)}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300"
            >
              {showCreate ? 'Cancel' : 'Create Campaign'}
            </button>
          </div>

          {showCreate && (
            <div data-testid="create-email-form" className="bg-white border border-slate-200 rounded-none p-8 mb-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>New Email Campaign</h3>
              <form onSubmit={handleCreate} className="space-y-6">
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Subject</label>
                  <input
                    data-testid="email-subject-input"
                    type="text"
                    value={newCampaign.subject}
                    onChange={(e) => setNewCampaign({...newCampaign, subject: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Recipients (comma-separated emails)</label>
                  <input
                    data-testid="email-recipients-input"
                    type="text"
                    value={newCampaign.recipients}
                    onChange={(e) => setNewCampaign({...newCampaign, recipients: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    placeholder="email1@example.com, email2@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Email Content (HTML)</label>
                  <textarea
                    data-testid="email-content-input"
                    value={newCampaign.html_content}
                    onChange={(e) => setNewCampaign({...newCampaign, html_content: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none font-mono text-sm"
                    rows="10"
                    placeholder="<h1>Your Email Content</h1>"
                    required
                  />
                </div>
                <button
                  data-testid="submit-email-campaign-btn"
                  type="submit"
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium transition-colors duration-300"
                >
                  Create Campaign
                </button>
              </form>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div data-testid="email-campaigns-list" className="space-y-4">
              {campaigns.map((campaign, index) => (
                <div
                  key={campaign.email_campaign_id}
                  data-testid={`email-campaign-card-${index}`}
                  className="bg-white border border-slate-200 rounded-none p-6 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>
                          {campaign.subject}
                        </h3>
                        <span className={`rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider border ${getStatusBadgeClass(campaign.status, "email")}`}>
                          {campaign.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">
                        <strong>Recipients:</strong> {campaign.recipients.length} email(s)
                      </p>
                      <p className="text-xs text-slate-500">
                        Created: {new Date(campaign.created_at).toLocaleString()}
                      </p>
                    </div>
                    {campaign.status === 'draft' && (
                      <button
                        data-testid={`send-campaign-${index}`}
                        onClick={() => sendCampaign(campaign.email_campaign_id)}
                        className="rounded-none bg-[#10B981] text-white hover:bg-[#059669] px-6 py-3 font-medium transition-colors duration-300 flex items-center gap-2"
                      >
                        <PaperPlaneTilt size={20} weight="fill" />
                        Send Campaign
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailMarketing;
